import { encryptAndUploadBlob, type BlobUploadProgress } from '../api/blobUpload';
import { formatBytes } from '../i18n/formatBytes';
import {
  encodeBase64,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
} from './primitives';
import {
  appendGuestManifestEntries,
  commitGuestManifestMutation,
} from './sharedFolderGuestManifest';
import {
  replaceFileContentsInManifest,
  type SharedFolderActorRef,
  type SharedFolderFileEntry,
  type SharedFolderManifestPlaintext,
} from './sharedFolderManifest';

const FILE_CONTENT_KEY_INFO = 'shareweb:folder-file-key:v1';
const FILE_CONTENT_KEY_AAD = 'shareweb:folder-file-key:v1';

async function wrapFileContentKey(
  folderKey: Uint8Array,
  fileId: string,
  contentKey: Uint8Array,
): Promise<string> {
  const material = await hkdfSha256(
    folderKey,
    new TextEncoder().encode(fileId),
    FILE_CONTENT_KEY_INFO,
    32,
  );
  const aesKey = await importAesKey(material);
  const encrypted = await encryptAesGcm(
    aesKey,
    contentKey,
    new TextEncoder().encode(FILE_CONTENT_KEY_AAD),
  );
  return encodeBase64(packEncryptedBlob(encrypted));
}

export async function uploadFileToSharedFolderAsGuest(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  currentPath: string;
  file: File;
  actor?: SharedFolderActorRef;
  onProgress?: (progress: BlobUploadProgress) => void;
}): Promise<{
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  conflicts: Array<{ originalPath: string; resolvedPath: string }>;
  guestUploadFilesUsed?: number;
  guestUploadBytesUsed?: number;
}> {
  const fileName = input.file.name.trim();
  if (!fileName) {
    throw new Error('empty_file_name');
  }

  const path = input.currentPath ? `${input.currentPath}/${fileName}` : fileName;

  const fileId = crypto.randomUUID();
  const { blobId, manifest: chunkManifest } = await encryptAndUploadBlob(input.file, {
    fileId,
    onProgress: input.onProgress,
  });
  const wrappedContentKey = await wrapFileContentKey(input.folderKey, fileId, chunkManifest.contentKey);
  const chunkCiphertextSizes = chunkManifest.chunks.map((chunk) => chunk.ciphertextSizeBytes);
  const fileEntry: SharedFolderFileEntry = {
    type: 'file',
    path,
    fileId,
    blobId,
    contentHash: chunkManifest.plaintextSha256,
    size: chunkManifest.totalPlaintextBytes,
    chunkPlaintextBytes: chunkManifest.chunkPlaintextBytes,
    chunkCount: chunkManifest.chunks.length,
    chunkCiphertextSizes: chunkManifest.chunks.length > 1 ? chunkCiphertextSizes : undefined,
    wrappedContentKey,
    contentType: input.file.type || undefined,
    createdAt: new Date().toISOString(),
    createdBy: input.actor,
    updatedAt: new Date().toISOString(),
    updatedBy: input.actor,
  };

  const committed = await appendGuestManifestEntries(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    [fileEntry],
  );

  return committed;
}

export async function replaceSharedFolderFileContentsAsGuest(input: {
  folderToken: string;
  guestInviteId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  fileId: string;
  file: Blob;
  actor?: SharedFolderActorRef;
}): Promise<{
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  entry: SharedFolderFileEntry;
  guestUploadFilesUsed?: number;
  guestUploadBytesUsed?: number;
}> {
  const existing = input.manifest.entries.find(
    (entry): entry is SharedFolderFileEntry => entry.type === 'file' && entry.fileId === input.fileId,
  );
  if (!existing) {
    throw new Error('file_deleted');
  }

  const { blobId, manifest: chunkManifest } = await encryptAndUploadBlob(input.file, {
    fileId: input.fileId,
  });
  const wrappedContentKey = await wrapFileContentKey(
    input.folderKey,
    input.fileId,
    chunkManifest.contentKey,
  );
  const chunkCiphertextSizes = chunkManifest.chunks.map((chunk) => chunk.ciphertextSizeBytes);
  const patch = {
    blobId,
    contentHash: chunkManifest.plaintextSha256,
    size: chunkManifest.totalPlaintextBytes,
    chunkPlaintextBytes: chunkManifest.chunkPlaintextBytes,
    chunkCount: chunkManifest.chunks.length,
    chunkCiphertextSizes: chunkManifest.chunks.length > 1 ? chunkCiphertextSizes : undefined,
    wrappedContentKey,
    contentType: input.file.type || existing.contentType,
    updatedAt: new Date().toISOString(),
    updatedBy: input.actor,
  };

  const committed = await commitGuestManifestMutation(
    input.folderToken,
    input.guestInviteId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => replaceFileContentsInManifest(latest, input.fileId, patch),
  );
  const entry = committed.manifest.entries.find(
    (item): item is SharedFolderFileEntry => item.type === 'file' && item.fileId === input.fileId,
  );
  if (!entry) {
    throw new Error('file_deleted');
  }

  return {
    revision: committed.revision,
    manifest: committed.manifest,
    entry,
    guestUploadFilesUsed: committed.guestUploadFilesUsed,
    guestUploadBytesUsed: committed.guestUploadBytesUsed,
  };
}

export function formatGuestUploadBudget(input: {
  maxGuestUploadFiles: number | null;
  maxGuestUploadTotalBytes: number | null;
  guestUploadFilesUsed: number;
  guestUploadBytesUsed: number;
}): string | null {
  const parts: string[] = [];
  const maxFiles = typeof input.maxGuestUploadFiles === 'number' ? input.maxGuestUploadFiles : null;
  const maxBytes = typeof input.maxGuestUploadTotalBytes === 'number'
    ? input.maxGuestUploadTotalBytes
    : null;

  if (maxFiles !== null) {
    const remaining = Math.max(0, maxFiles - input.guestUploadFilesUsed);
    parts.push(`${remaining} files`);
  }

  if (maxBytes !== null) {
    const remainingBytes = Math.max(0, maxBytes - input.guestUploadBytesUsed);
    parts.push(formatBytes(remainingBytes));
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function guestCanUpload(input: {
  permission: string;
  maxGuestUploadFiles: number | null;
  maxGuestUploadTotalBytes: number | null;
  guestUploadFilesUsed: number;
  guestUploadBytesUsed: number;
}): boolean {
  if (input.permission !== 'ReadWrite') {
    return false;
  }

  const maxFiles = typeof input.maxGuestUploadFiles === 'number' ? input.maxGuestUploadFiles : null;
  const maxBytes = typeof input.maxGuestUploadTotalBytes === 'number'
    ? input.maxGuestUploadTotalBytes
    : null;

  if (maxFiles !== null && input.guestUploadFilesUsed >= maxFiles) {
    return false;
  }

  if (maxBytes !== null && input.guestUploadBytesUsed >= maxBytes) {
    return false;
  }

  return true;
}
